/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FC,
  HTMLInputTypeAttribute,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  TextField,
  Box,
  useTheme,
  Theme,
  BoxProps,
  SxProps,
  InputAdornment,
  Tooltip,
} from '@mui/material'
import {
  FieldError,
  RegisterOptions,
  UseFormRegister,
  useController,
  useFormContext,
} from 'react-hook-form'
import { LoadedFormLabel } from '../display'
import {
  formatCurrency,
  formatPercent,
  formatNumber,
  getThousandsSeparator,
} from 'src/components/common/helpers'
import { regExps } from '$constants'
import {
  InlineInputIcon,
  InputWithInlineIcon,
} from './CustomInputBaseComponent'
import {
  formatTextFieldValueByVariant,
  transformTextFieldNumberOnChange,
} from './helper'

const numberVariants = ['currency', 'hour', 'number', 'percent'] as const
type Variant = 'input' | (typeof numberVariants)[number]
type NumberVariant = (typeof numberVariants)[number]

const isNumberVariant = (v: Variant | undefined): v is NumberVariant =>
  !!v && numberVariants.includes(v as NumberVariant)

type LoadedTextFieldProps = {
  name: string
  label?: ReactNode
  placeholder?: string
  type?: HTMLInputTypeAttribute
  multiline?: boolean
  onChange?: (...args: any) => any
  onBufferingChange?: (...args: any) => any
  onBlur?: (...args: any) => any
  onFocus?: (...args: any) => any
  onKeyDown?: (...args: any) => any
  labelBoxProps?: BoxProps
  labelPosition?: 'left' | 'above'
  // The layout props is used to know if the text field is in a grid but without applying
  // the styles for the grid as they are for the Invoice page and they might need to be refactored
  layout?: 'grid' | 'form'
  textFieldSxProps?: SxProps<Theme>
  additiveTextFieldSxProps?: SxProps<Theme>
  containerSxProps?: SxProps<Theme>
  startAdornment?: ReactNode
  endAdornment?: ReactNode
  variant?: Variant
  size?: 'small' | 'medium' | 'large'
  labelSxProps?: SxProps<Theme>
  fullWidth?: boolean
  inputProps?: any
  // This isInGrid prop is used to determine if the input is in a grid or not
  // and also it is used to remove the input styles (which we only do in EditInvoice page so far)
  isInGrid?: boolean
  tabIndex?: number
  // This could be a 0-100 based on the NumberParser but we shouldn't need more than these DP in a textfield.
  // So limiting to these options
  numberPrecision?: 0 | 1 | 2 | 3
  autoComplete?: string
  /**
   * Allow an icon to be shown inside the input, immediately to the right of the input content.
   * The tooltip message will be displayed when the icon is hovered over.
   */
  inlineInputIcon?: InlineInputIcon
  hasFocus?: boolean
  autoFocus?: boolean
  isFocused?: boolean
  onlyPostiveNumbers?: boolean
  highlightOnClick?: boolean
  inputTooltip?: string
  isError?: boolean
  helperText?: string
} & RegisterOptions<any>

const LoadedTextFieldBufferingTimeout: number = 500

const LoadedTextField: FC<LoadedTextFieldProps> = (props) => {
  const { value } = props

  // If the value is undefined, then the component is being used in a form
  return value === undefined ? (
    <FormTextField {...props} />
  ) : (
    <SelfContainedTextField {...props} />
  )
}

export default LoadedTextField

const FormTextField: FC<LoadedTextFieldProps> = (props) => {
  const {
    name,
    onChange,
    onFocus,
    onBlur,
    disabled,
    variant,
    numberPrecision,
    onKeyDown,
    onlyPostiveNumbers,
  } = props
  const { control, register, setValue, setError, clearErrors } =
    useFormContext()
  const {
    field: { value },
    fieldState: { error },
  } = useController({ control, name })

  const [isFocused, setIsFocused] = useState<boolean>(false)

  const onChangeCurrencyHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.currencyInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onChangePercentHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.percentInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onChangeNumberHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.numberInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onChangeHandler = (event: { target: { value: string } }) => {
    const newValue =
      variant === 'currency'
        ? onChangeCurrencyHandler(event.target.value)
        : variant === 'percent'
        ? onChangePercentHandler(event.target.value)
        : variant === 'number'
        ? onChangeNumberHandler(event.target.value)
        : variant === 'hour'
        ? onChangePercentHandler(event.target.value)
        : ''

    setValue(name, newValue)

    if (onChange) {
      onChange(event)
    }
  }

  const onBlurHandler = (event: { target: { value: string } }) => {
    const newValue = formatTextFieldValueByVariant(
      event.target.value,
      numberPrecision ?? 0,
      variant
    )

    if (isNumberVariant(variant)) {
      const re = new RegExp(getThousandsSeparator(), 'g')
      const result =
        newValue != undefined && newValue != ''
          ? +String(newValue).replace(re, '')
          : null
      setValue(name, result)

      if (props.required) {
        if (result == undefined) {
          setError(name, { type: 'required' })
        } else {
          clearErrors(name)
        }
      }
    }

    onBlur?.(event)
    setIsFocused(false)
  }

  const onFocusHandler = (e) => {
    setIsFocused(true)
    // When the variant is currency, we want to remove any thousands separators
    // that are present in the formatted value in the focused input. If we
    // don't do this, when the user starts editing a value that has a thousands
    // separator present the cursor will jump to the end of the input as soon
    // as they presss a key due to the currency formatting logic in the onChange
    // handler being triggered and it updating the input to have the new formatted
    // value.
    //
    // What this means is that when a field has a value like '1,000' and the user
    // clicks into the input, the input value will automatically change to '1000'
    // to allow them to edit the unformatted value.
    //
    // If this behaviour proves undesirable from a product POV then we need to
    // update the onChange handler so that it keeps track of the cursor position
    // and calls setSelectionRange(cursorPosition, cursorPosition) after any change
    // is made.

    if (variant === 'currency') {
      const re = new RegExp(getThousandsSeparator(), 'g')
      setValue(
        name,
        value != undefined ? +String(value).replace(re, '') : value
      )
    }

    onFocus?.(e)
  }

  return (
    <InternalTextField
      {...props}
      value={value}
      error={error}
      disabled={disabled}
      register={register}
      onChange={
        variant === 'currency' ||
        variant === 'percent' ||
        variant === 'number' ||
        variant === 'hour'
          ? onChangeHandler
          : onChange
      }
      onBlur={
        variant === 'currency' ||
        variant === 'percent' ||
        variant === 'number' ||
        variant === 'hour'
          ? onBlurHandler
          : onBlur
      }
      onFocus={onFocusHandler}
      isFocused={isFocused}
      onKeyDown={onKeyDown}
    />
  )
}

export const SelfContainedTextField: FC<LoadedTextFieldProps> = (props) => {
  const {
    value,
    onBufferingChange,
    onChange,
    onBlur,
    onFocus,
    required,
    variant,
    onlyPostiveNumbers,
  } = props
  const [insertedValue, setInsertedValue] = useState<string>(value || '')
  const [isError, setIsError] = useState<FieldError | undefined>()
  const [isFocused, setIsFocused] = useState(false)
  const bufferingTimeout = useRef<ReturnType<typeof setTimeout>>()

  const onChangeCurrencyHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.currencyInput, '')
    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onChangePercentHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.percentInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const onChangeNumberHandler = (value: string) => {
    //This prevents the user to insert letters and symbols in the input
    const newValue = value.replace(regExps.numberInput, '')

    return transformTextFieldNumberOnChange(newValue, onlyPostiveNumbers)
  }

  const handleOnChange = async (event: any) => {
    let updatedValue = event.target.value

    if (required) {
      const isEmpty = updatedValue.trim() === ''
      setIsError(isEmpty ? { type: 'required' } : undefined)
    }

    if (variant === 'currency') {
      updatedValue = onChangeCurrencyHandler(event.target.value)
    } else if (variant === 'percent') {
      updatedValue = onChangePercentHandler(event.target.value)
    } else if (variant === 'number') {
      updatedValue = onChangeNumberHandler(event.target.value)
    } else if (variant === 'hour') {
      updatedValue = onChangePercentHandler(event.target.value)
    }

    event.target.value = updatedValue
    if (onBufferingChange) {
      if (bufferingTimeout.current) {
        clearTimeout(bufferingTimeout.current)
      }
      bufferingTimeout.current = setTimeout(() => {
        updatedValue = onBufferingChange(event)
      }, LoadedTextFieldBufferingTimeout)
    }

    onChange?.(event)
    setInsertedValue(updatedValue)
  }

  const onBlurHandler = (event: { target: { value: string } }) => {
    // Don't store the formatted value, store the raw value
    const rawValue = event.target.value // Keep the raw input
    setInsertedValue(rawValue) // Store raw value, not formatted

    onBlur?.(event)
    setIsFocused(false)
  }

  const onFocusHandler = (event: { target: { value: string } }) => {
    // When focusing, show the raw unformatted value for editing
    if (value != null) {
      setInsertedValue(String(value)) // Always use the raw numeric value for editing
    }

    onFocus?.(event)
    setIsFocused(true)
  }

  useEffect(() => {
    setInsertedValue(value || '')
  }, [value])

  return (
    <InternalTextField
      {...props}
      value={insertedValue}
      onChange={handleOnChange}
      onBlur={onBlurHandler}
      onFocus={onFocusHandler}
      error={isError}
      isFocused={isFocused}
    />
  )
}

type SelfContainedInternalTextFieldProps = LoadedTextFieldProps & {
  error?: FieldError
  helperText?: string
  register?: UseFormRegister<any>
}

const InternalTextField: FC<SelfContainedInternalTextFieldProps> = ({
  name,
  value,
  variant,
  label,
  placeholder,
  type = 'text',
  multiline = false,
  labelBoxProps,
  labelPosition = 'left',
  textFieldSxProps,
  additiveTextFieldSxProps,
  containerSxProps,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  error,
  helperText,
  isError,
  register,
  startAdornment,
  endAdornment,
  size,
  labelSxProps,
  fullWidth,
  disabled,
  inputProps,
  isInGrid = false,
  tabIndex,
  autoComplete,
  inlineInputIcon,
  layout,
  hasFocus,
  autoFocus,
  numberPrecision,
  highlightOnClick,
  inputTooltip,
  ...registerOptions
}) => {
  inputProps = { ...inputProps, 'data-testid': name }
  const theme = useTheme<Theme>()
  const inputRef = useRef<HTMLInputElement>(null)

  /**
   * Highlights the input content when the user clicks on the text field
   * so the user can immediately start typing
   */
  const handleClick = () => {
    if (
      inputRef.current &&
      (isInGrid ||
        highlightOnClick ||
        (variant &&
          ['currency', 'percent', 'number', 'hour'].includes(variant)))
    ) {
      inputRef.current.select()
    }
  }

  // If the user has specified a inline input icon, check if it should be shown
  // and if so update the text field styles to ensure the icon sits immediately
  // to the right of the input content.
  if (inlineInputIcon?.show(value)) {
    textFieldSxProps = {
      ...textFieldSxProps,
      '& .MuiInputBase-input': {
        fieldSizing: value ? 'content' : 'unset',
        width: 'unset',
      },
    }
  }

  // Prevents event bubbling on key presses we expect to occur within a text input
  // while editing it.
  const onKeyDownStopPropagation = (e) => {
    const aKeyCode = 65
    const spaceKeyCode = 32

    if (
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'Backspace' ||
      // Ctrl-A or Cmd-A for selecting all
      (e.ctrlKey && e.keyCode === aKeyCode) ||
      (e.metaKey && e.keyCode === aKeyCode) ||
      e.keyCode === spaceKeyCode
    ) {
      e.stopPropagation()
    }
  }

  let formattedValue = value ?? ''

  if (inputRef.current != document.activeElement) {
    if (variant === 'currency' && value != null) {
      formattedValue = formatCurrency(+value, true)
    } else if (variant === 'percent' && value != null) {
      formattedValue = formatPercent(+value, true)
    } else if (variant === 'number' && value) {
      formattedValue = formatNumber(formattedValue, numberPrecision)
    } else if (variant === 'hour' && value != null) {
      formattedValue = formatPercent(+value, true)
    }
  }

  return (
    <Box
      display={'flex'}
      flex={fullWidth ? 1 : 'unset'}
      flexDirection={labelPosition === 'left' ? 'row' : 'column'}
      alignItems={labelPosition === 'left' ? 'center' : 'self-start'}
      sx={{
        ...(fullWidth && { width: '100%' }),
        ...containerSxProps,
      }}
    >
      <Box minHeight={'1.884rem'} {...labelBoxProps}>
        {label && (
          <LoadedFormLabel
            label={label}
            labelPosition={labelPosition}
            sx={labelSxProps}
          />
        )}
      </Box>
      <Tooltip arrow placement="top" title={inputTooltip ?? ''}>
        <TextField
          id={`text-field-${name}`}
          variant="outlined"
          type={type}
          focused={hasFocus}
          multiline={multiline}
          disabled={disabled}
          fullWidth={fullWidth}
          tabIndex={tabIndex}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          inputRef={inputRef}
          inputProps={inputProps}
          {...(placeholder && { placeholder: placeholder })}
          sx={{
            ...(additiveTextFieldSxProps as any),
            '.MuiInputBase-inputMultiline': {
              overflow: 'auto !important', // Ensure scrolling behavior when it is a multiline/textarea input
            },

            '& .Mui-focused': {
              // make sure that the input has a border when in the grid and focused
              ...(isInGrid && {
                outline: (theme) => `1px solid ${theme.palette.grey[400]}`,
                outlineOffset: '2px',
              }),
              // push the input and the icon apart when focused in the grid with
              // an inline input icon
              ...(isInGrid &&
                inlineInputIcon && { justifyContent: 'space-between' }),

              // we hide the input icon while focused to avoid any overflow issues
              '& .LoadedIcon-with-tooltip': {
                display: 'none',
              },
            },
            '& .MuiInputBase-root': {
              ...additiveTextFieldSxProps?.['& .MuiInputBase-root'],
              ...(!fullWidth && {
                ...(size === 'small' && { width: '10rem' }),
                ...(size === 'large' && { width: '27rem' }),
              }),

              ...(multiline && {
                ...{
                  alignItems: 'flex-start !important',
                  flexFlow: 'column !important',
                },
              }),
              ...(isInGrid && {
                paddingLeft: '0px',
                '&.Mui-focused': {
                  paddingLeft: '12px',
                },
                '.MuiOutlinedInput-notchedOutline': {
                  border: 0,
                },
                ...(disabled
                  ? {
                      '& .MuiOutlinedInput-input.Mui-disabled': {
                        WebkitTextFillColor: 'unset',
                        color: theme.palette.text.primary,
                      },
                      '.MuiTypography-root': {
                        color: theme.palette.text.primary,
                      },
                    }
                  : {
                      '& .MuiInputBase-input': {
                        color: `${theme.palette.primary.main} !important`,
                      },
                    }),
              }),
            },

            ...textFieldSxProps,
          }}
          InputProps={{
            ...({
              ...(startAdornment && {
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      ...(isInGrid && { '& p': { color: 'primary.main' } }),
                      marginRight: 0,
                      paddingRight: '0.2rem',
                    }}
                  >
                    {startAdornment}
                  </InputAdornment>
                ),
              }),
              ...(endAdornment && {
                endAdornment: (
                  <InputAdornment position="end">{endAdornment}</InputAdornment>
                ),
              }),
              ...(inlineInputIcon && {
                inputComponent: InputWithInlineIcon,
                // this inputProps is the key to the inputComponent above not re-rendering,
                // we can't have a function for inputComponent to pass props, else each
                // change will cause a re render
                inputProps: {
                  ...inputProps,
                  inputRef,
                  message:
                    typeof inlineInputIcon.tooltipMessage === 'function'
                      ? inlineInputIcon.tooltipMessage(value)
                      : inlineInputIcon?.tooltipMessage,

                  Icon: inlineInputIcon.show(value)
                    ? typeof inlineInputIcon.Icon === 'function'
                      ? inlineInputIcon.Icon(value)
                      : inlineInputIcon?.Icon
                    : undefined,
                  iconProps: {
                    ...inlineInputIcon.iconProps,
                    sx: {
                      ...inlineInputIcon.iconProps?.sx,
                    },
                  },
                },
              }),
            } as any),
          }}
          // Using only local state for error display since React Hook Form is not used here
          error={isError}
          helperText={helperText}
          value={formattedValue}
          {...(error && {
            helperText: error ? error.message : null,
            error: !!error,
          })}
          {...(!register && onChange && { onChange: onChange })}
          {...(!register && onBlur && { onBlur: onBlur })}
          onFocus={onFocus}
          onKeyDown={(event: React.KeyboardEvent) => {
            // Trigger blur when Enter key is pressed - it doesn't matter
            // if the input is in a grid or not
            if (event.key === 'Enter' && !multiline) {
              if (inputRef.current) {
                inputRef.current.blur()
              }
            }

            if (isInGrid || layout === 'grid') onKeyDownStopPropagation(event)

            onKeyDown?.(event)
          }}
          {...(register && {
            ...register(name, {
              onChange: onChange,
              onBlur: onBlur,
              ...registerOptions,
            }),
          })}
          onClick={handleClick}
        />
      </Tooltip>
    </Box>
  )
}
